import { TextField } from "@mui/material"
import { styled } from "@mui/system"

const DATE_PICKER_SIZES = {
    compact: {
        height: 48,
        padding: "14px 12px 4px 12px",
        inputFontSize: "14px",
        inputLineHeight: "20px",
        labelFontSize: "14px",
        labelTransform: "translate(12px, 8px) scale(1)",
        labelShrinkTransform: "translate(12px, 2px) scale(0.75)",
        iconPadding: 4,
    },
    sm: {
        height: 48,
        padding: "14px 12px 4px 12px",
        inputFontSize: "12px",
        inputLineHeight: "18px",
        labelFontSize: "12px",
        labelTransform: "translate(12px, 9px) scale(1)",
        labelShrinkTransform: "translate(12px, 3px) scale(0.75)",
        iconPadding: 4,
    },
    md: {
        height: 56,
        padding: "18px 12px 6px 16px",
        inputFontSize: "14px",
        inputLineHeight: "20px",
        labelFontSize: "14px",
        labelTransform: "translate(16px, 10px) scale(1)",
        labelShrinkTransform: "translate(16px, 4px) scale(0.75)",
        iconPadding: 6,
    },
    xl: {
        height: 64,
        padding: "22px 14px 8px 20px",
        inputFontSize: "16px",
        inputLineHeight: "22px",
        labelFontSize: "16px",
        labelTransform: "translate(20px, 11px) scale(1)",
        labelShrinkTransform: "translate(20px, 5px) scale(0.75)",
        iconPadding: 8,
    },
}

export const StyledDatePicker = styled(TextField, {
    shouldForwardProp: (prop) => prop !== "pickerSize",
})(({ theme, pickerSize = "md" }) => {
    const size = DATE_PICKER_SIZES[pickerSize] ?? DATE_PICKER_SIZES.md
    const errorColor = theme.palette.error.light

    return ({
    /* ===== Filled root ===== */
    "& .MuiFilledInput-root": {
        backgroundColor: theme.palette.input.background.primary,
        color: theme.palette.primary.contrastText,
        borderRadius: 0,
        border: `1px solid ${theme.palette.input.borderColor.primary}`,

        /* Layout/spacing (label separado do conteúdo) */
        height: size.height,
        display: "flex",
        alignItems: "flex-end",
        padding: size.padding,

        "&:hover": {
            backgroundColor: theme.palette.input.background.secondary,
            border: `1px solid ${theme.palette.primary.main2}`,
        },

        "&.Mui-focused:not(.Mui-error)": {
            backgroundColor: theme.palette.input.background.secondary,
            border: `1px solid ${theme.palette.primary.main2}`,
        },

        "&.Mui-error": {
            backgroundColor: theme.palette.input.background.primary,
            border: `1px solid ${errorColor}`,
        },

        /* remove underline do Filled */
        "&:before, &:after": {
            display: "none",
        },

        /* ícones */
        "& svg": {
            color: theme.palette.icon.main,
        },

        "&.Mui-error svg": {
            color: errorColor,
        },

        "& input:-webkit-autofill": {
            backgroundColor: "rgba(210, 159, 9, 0.3) !important",
            transition: "background-color 5000s ease-in-out 0s",
            WebkitTextFillColor: `${theme.palette.primary.contrastText} !important`,
            caretColor: `${theme.palette.primary.contrastText} !important`,
        },
    },

    /* ===== Input ===== */
    "& .MuiFilledInput-input": {
        color: theme.palette.common.white,
        fontSize: size.inputFontSize,
        padding: 0, // root controla padding
        lineHeight: size.inputLineHeight,
    },

    /* ===== Label (mais separada do conteúdo) ===== */
    "& .MuiInputLabel-root": {
        color: theme.palette.common.white,
        opacity: 0.7,
        fontSize: size.labelFontSize,
        left: 0,
        transform: size.labelTransform,
    },

    "& .MuiInputLabel-root.Mui-focused": {
        color: theme.palette.common.white,
        opacity: 0.7,
    },

    "& .MuiInputLabel-root.Mui-error": {
        color: theme.palette.common.white,
        opacity: 0.7,
    },

    "& .MuiInputLabel-shrink": {
        transform: size.labelShrinkTransform,
    },

    /* ===== Adornments / Calendar Icon ===== */
    "& .MuiInputAdornment-root": {
        marginTop: "0 !important",
        height: "100%",
        display: "flex",
        alignItems: "center",
        "& svg": {
            display: "block",
            verticalAlign: "middle",
        },
    },

    "& .MuiIconButton-root": {
        padding: size.iconPadding,
    },

    "& .MuiInputAdornment-positionStart": {
        marginLeft: 0,
        height: "100%",
        alignItems: "center",
    },

    /* ===== Helper text ===== */
    "& .MuiFormHelperText-root": {
        marginTop: 8,
        marginLeft: 0,
        fontFamily: theme.typography.fontFamily,
        fontWeight: theme.typography.fontWeights.regular,
        fontStyle: "normal",
        fontSize: theme.typography.fontSizes.sm,
        lineHeight: "100%",
        letterSpacing: "0%",
        color: theme.palette.text.disabled,
    },

    "& .MuiFormHelperText-root.Mui-error": {
        color: errorColor,
    },
    })
})
